import { useQueryClient } from '@tanstack/react-query';
import { useEffect, type JSX } from 'react';

import { useSessionStore } from '../auth/session-store';
import { onSignaturesSynced, queueController } from './use-signature-queue';

/**
 * Mounted once: syncs the signature outbox on app start, when the connection
 * returns, every minute while something is pending, and again whenever a
 * different user (or the same one after logging in again) holds the session.
 */
export function OfflineSyncRunner(): JSX.Element | null {
  const queryClient = useQueryClient();
  const userId = useSessionStore((state) => state.session?.userId ?? null);

  useEffect(
    () => onSignaturesSynced(() => void queryClient.invalidateQueries({ queryKey: ['deliveries'] })),
    [queryClient],
  );

  useEffect(() => {
    const stop = queueController.start();

    return stop;
  }, []);

  useEffect(() => {
    // New session: show that user's items and try to deliver them.
    // An unreadable outbox must not surface as an unhandled rejection; the next trigger retries.
    void queueController
      .refresh()
      .then(() => queueController.syncNow())
      .catch(() => undefined);
  }, [userId]);

  return null;
}
